import React from 'react';
import { Box, Typography, Tooltip, Avatar } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';

function AvatarFrameCard({ frame, unlocked = false, selected = false, onClick, hideName = false, previewAvatar = null }) {

  // 20种霓虹色 - 手动定义，不使用渐变
  const neonColors = [
    { r: 255, g: 20, b: 147 },    // 0: 霓虹粉 (Deep Pink)
    { r: 0, g: 255, b: 127 },     // 1: 霓虹绿 (Spring Green)
    { r: 0, g: 191, b: 255 },     // 2: 霓虹蓝 (Deep Sky Blue)
    { r: 255, g: 215, b: 0 },     // 3: 霓虹金 (Gold)
    { r: 255, g: 20, b: 20 },     // 4: 霓虹红 (Red)
    { r: 138, g: 43, b: 226 },    // 5: 霓虹紫 (Blue Violet)
    { r: 0, g: 255, b: 255 },     // 6: 霓虹青 (Cyan)
    { r: 255, g: 105, b: 180 },   // 7: 霓虹粉红 (Hot Pink)
    { r: 50, g: 205, b: 50 },     // 8: 霓虹青绿 (Lime Green)
    { r: 30, g: 144, b: 255 },    // 9: 霓虹天蓝 (Dodger Blue)
    { r: 255, g: 140, b: 0 },     // 10: 霓虹橙 (Dark Orange)
    { r: 186, g: 85, b: 211 },    // 11: 霓虹紫罗兰 (Medium Orchid)
    { r: 64, g: 224, b: 208 },    // 12: 霓虹青绿 (Turquoise)
    { r: 255, g: 69, b: 0 },      // 13: 霓虹红橙 (Red Orange)
    { r: 147, g: 112, b: 219 },   // 14: 霓虹中紫 (Medium Slate Blue)
    { r: 0, g: 250, b: 154 },     // 15: 霓虹薄荷绿 (Medium Spring Green)
    { r: 255, g: 0, b: 255 },     // 16: 霓虹洋红 (Magenta)
    { r: 32, g: 178, b: 170 },    // 17: 霓虹浅海绿 (Light Sea Green)
    { r: 255, g: 165, b: 0 },     // 18: 霓虹橙 (Orange)
    { r: 148, g: 0, b: 211 }      // 19: 霓虹紫 (Dark Violet)
  ];

  // Get neon color for frame based on badge count
  const getNeonColor = (badgeCount) => {
    const index = Math.min(badgeCount, 19);
    return neonColors[index];
  };

  // Generate frame style with unique neon color for each frame
  const getFrameStyle = () => {
    const badgeCount = frame.requiredBadges || 0;
    const color = getNeonColor(badgeCount);
    const rgbString = `rgb(${color.r}, ${color.g}, ${color.b})`;
    const rgbaString = (alpha) => `rgba(${color.r}, ${color.g}, ${color.b}, ${alpha})`;
    
    // Calculate border width and shadow intensity based on badge count
    const borderWidth = 2 + Math.floor(badgeCount / 5); // 2-5px
    const shadowIntensity = 0.3 + (badgeCount * 0.025); // 0.3-0.775
    const shadowSize = 4 + (badgeCount * 0.8); // 4-19.2px
    
    // Higher badge counts get more effects
    if (badgeCount < 5) {
      // Simple frames (0-4)
      return {
        border: `${borderWidth}px solid ${rgbString}`,
        boxShadow: `0 0 ${shadowSize}px ${rgbaString(shadowIntensity)}`,
        backgroundColor: 'transparent'
      };
    } else if (badgeCount < 10) {
      // Enhanced frames (5-9)
      return {
        border: `${borderWidth}px solid ${rgbString}`,
        boxShadow: `0 0 ${shadowSize}px ${rgbaString(shadowIntensity)}, inset 0 0 ${shadowSize * 0.6}px ${rgbaString(0.1)}`,
        backgroundImage: `linear-gradient(135deg, ${rgbaString(0.1)}, ${rgbaString(0.15)})`,
        backgroundColor: 'transparent'
      };
    } else if (badgeCount < 15) {
      // Epic frames (10-14) with gradient animation
      return {
        border: `${borderWidth}px solid ${rgbString}`,
        boxShadow: `0 0 ${shadowSize}px ${rgbaString(shadowIntensity)}, 0 0 ${shadowSize * 1.5}px ${rgbaString(shadowIntensity * 0.5)}, inset 0 0 ${shadowSize * 0.7}px ${rgbaString(0.15)}`,
        backgroundImage: `linear-gradient(135deg, ${rgbaString(0.15)}, ${rgbaString(0.25)}, ${rgbaString(0.15)})`,
        backgroundColor: 'transparent',
        backgroundSize: '200% 200%',
        '@keyframes gradientShift': {
          '0%, 100%': {
            backgroundPosition: '0% 50%'
          },
          '50%': {
            backgroundPosition: '100% 50%'
          }
        },
        animation: 'gradientShift 3s ease infinite'
      };
    } else {
      // Legendary frames (15-19) with pulse and gradient animation
      return {
        border: `${borderWidth}px solid ${rgbString}`,
        boxShadow: `0 0 ${shadowSize}px ${rgbaString(shadowIntensity)}, 0 0 ${shadowSize * 2}px ${rgbaString(shadowIntensity * 0.6)}, 0 0 ${shadowSize * 3}px ${rgbaString(shadowIntensity * 0.3)}, inset 0 0 ${shadowSize}px ${rgbaString(0.2)}`,
        backgroundImage: `linear-gradient(135deg, ${rgbaString(0.2)}, ${rgbaString(0.3)}, ${rgbaString(0.2)})`,
        backgroundColor: 'transparent',
        backgroundSize: '200% 200%',
        '@keyframes pulse': {
          '0%, 100%': {
            boxShadow: `0 0 ${shadowSize}px ${rgbaString(shadowIntensity)}, 0 0 ${shadowSize * 2}px ${rgbaString(shadowIntensity * 0.6)}, 0 0 ${shadowSize * 3}px ${rgbaString(shadowIntensity * 0.3)}`
          },
          '50%': {
            boxShadow: `0 0 ${shadowSize * 1.3}px ${rgbaString(shadowIntensity * 1.2)}, 0 0 ${shadowSize * 2.5}px ${rgbaString(shadowIntensity * 0.8)}, 0 0 ${shadowSize * 4}px ${rgbaString(shadowIntensity * 0.5)}`
          }
        },
        '@keyframes gradientShift': {
          '0%, 100%': {
            backgroundPosition: '0% 50%'
          },
          '50%': {
            backgroundPosition: '100% 50%'
          }
        },
        animation: 'pulse 2s ease-in-out infinite, gradientShift 3s ease infinite'
      };
    }
  };

  const frameStyle = getFrameStyle();
  
  // Get preview avatar image or use default
  const previewImageUrl = previewAvatar?.imageUrl || previewAvatar || null;

  return (
    <Tooltip 
      title={
        unlocked 
          ? frame.description || frame.name
          : `需要 ${frame.requiredBadges} 个徽章解锁`
      } 
      arrow
    >
      <Box
        onClick={unlocked ? onClick : undefined}
        sx={{
          textAlign: 'center',
          opacity: unlocked ? 1 : 0.4,
          transition: 'all 0.3s',
          cursor: unlocked ? 'pointer' : 'default',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          '&:hover': {
            transform: unlocked ? 'scale(1.05)' : 'none',
            opacity: 1
          }
        }}
      >
        {selected && (
          <Box
            sx={{
              position: 'absolute',
              top: -4,
              right: -4,
              width: 20,
              height: 20,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2,
              boxShadow: 2
            }}
          >
            <Typography variant="caption" sx={{ color: 'white', fontSize: '0.7rem' }}>✓</Typography>
          </Box>
        )}

        {!unlocked && (
          <Box
            sx={{
              position: 'absolute',
              top: -4,
              left: -4,
              zIndex: 2,
              bgcolor: 'background.paper',
              borderRadius: '50%',
              p: 0.5,
              boxShadow: 1
            }}
          >
            <LockIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
          </Box>
        )}
        
        {/* Avatar with frame preview */}
        <Box
          sx={{
            width: '100%',
            aspectRatio: '1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 1,
            position: 'relative',
            filter: unlocked ? 'none' : 'grayscale(100%) brightness(0.5)',
            transition: 'filter 0.3s'
          }}
        >
          {/* Frame container with style - wraps the avatar */}
          <Box
            sx={{
              display: 'inline-flex',
              borderRadius: '50%',
              padding: frame.rarity === 'legendary' ? '4px' : '3px',
              width: '100%',
              height: '100%',
              maxWidth: '100%',
              maxHeight: '100%',
              ...frameStyle,
              transition: 'all 0.3s'
            }}
          >
            <Avatar
              src={previewImageUrl}
              alt="Preview"
              sx={{
                width: '100%',
                height: '100%',
                bgcolor: 'primary.main',
                fontSize: '1.2rem',
                border: 'none'
              }}
            >
              {previewImageUrl ? null : '👤'}
            </Avatar>
          </Box>
        </Box>
        
        {!hideName && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontWeight: 600,
              color: unlocked ? 'text.primary' : 'text.secondary',
              mb: 0.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {frame.name}
          </Typography>
        )}
        
        {!unlocked && (
          <Typography 
            variant="caption" 
            color="text.secondary" 
            sx={{ 
              fontSize: '0.65rem',
              display: 'block',
              textAlign: 'center'
            }}
          >
            需要 {frame.requiredBadges} 个徽章
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}

export default AvatarFrameCard;
